import React from 'react';
import { motion } from 'framer-motion';
import { Check, Flame, ThermometerSun, Snowflake, Wind, Wrench, Factory } from 'lucide-react';
import { CTASection } from '../components/CTASection';
import { Link } from 'react-router-dom';
export function Services() {
  const features = ['Full turnkey hire solution', 'Surveying through to commissioning', 'BMS equipped for complete visibility', 'Fuel Management services available', 'Within 24 hours emergency response', 'Nationwide coverage, 24/7', 'Gas, Gasoil, and HVO sustainable fuel options', 'FDA approved hoses available'];
  return <main className="flex-grow" data-id="element-487">
      {/* Hero Banner */}
      <div className="relative py-24 bg-brand-dark overflow-hidden" data-id="element-488">
        <div className="absolute inset-0" data-id="element-489">
          <img src="https://rapidenergy.co.uk/wp-content/uploads/2026/03/Rapid-Energy-van-next-to-packaged-boiler.webp" alt="Rapid Energy Equipment" className="w-full h-full object-cover opacity-20" data-id="element-490" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark to-brand-dark/50" data-id="element-491"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-492">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-heading font-extrabold text-white mb-6" data-id="element-493">
            Our <span className="text-brand-green" data-id="element-494">Services</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-495">
            Comprehensive temporary heating and cooling solutions
          </motion.p>
        </div>
      </div>

      {/* Featured Service: Boiler Hire */}
      <section className="py-20 bg-white" data-id="element-496">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-497">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-498">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:w-1/2" data-id="element-499">
              <div className="inline-flex items-center space-x-2 bg-brand-light px-4 py-2 rounded-full mb-6 border border-gray-200" data-id="element-500">
                <Flame className="w-4 h-4 text-brand-green" data-id="element-501" />
                <span className="text-brand-dark text-sm font-bold uppercase tracking-wide" data-id="element-502">
                  Featured Service
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark mb-6" data-id="element-503">
                Temporary Boiler Hire
              </h2>

              <div className="space-y-4 text-gray-600 text-lg mb-8" data-id="element-504">
                <p data-id="element-505">
                  Our fleet of temporary boilers boast impressive energy
                  efficiencies and reliability. Combined with experienced,
                  accredited and certified engineers, delivering a reliable,
                  excellent service comes as standard with every hire we
                  undertake.
                </p>
                <p data-id="element-506">
                  We offer our clients a complete{' '}
                  <strong data-id="element-507">full turnkey hire solution</strong>, from surveying
                  through to commissioning a temporary boiler. Our team of
                  engineers and project managers will become an extension of
                  your team and project.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-508">
                {features.map((feature, i) => <div key={i} className="flex items-start" data-id="element-509">
                    <div className="bg-brand-green/20 p-1 rounded-full mr-3 mt-1 flex-shrink-0" data-id="element-510">
                      <Check className="w-4 h-4 text-brand-dark" data-id="element-511" />
                    </div>
                    <span className="text-sm font-medium text-brand-dark" data-id="element-512">
                      {feature}
                    </span>
                  </div>)}
              </div>

              <Link to="/boiler-hire" className="inline-block bg-brand-dark text-white font-bold py-4 px-8 rounded-lg hover:bg-brand-green hover:text-brand-dark transition-colors duration-300" data-id="element-513">
                BROWSE BOILERS
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:w-1/2" data-id="element-514">
              <img src="https://rapidenergy.co.uk/wp-content/uploads/2026/03/1.5MW-Packaged-Boiler-N1-1.webp" alt="1.5MW Packaged Boiler" className="rounded-2xl shadow-2xl w-full object-cover h-[600px]" data-id="element-515" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Services Grid */}
      <section className="py-20 bg-brand-light" data-id="element-516">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-517">
          <div className="text-center mb-16" data-id="element-518">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark" data-id="element-519">
              Explore All Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-520">
            {[{
            title: 'Heater Hire',
            icon: ThermometerSun,
            desc: 'Electric, indirect fired and direct fired heaters for commercial and industrial applications.',
            path: '/heater-hire'
          }, {
            title: 'Chiller Hire',
            icon: Snowflake,
            desc: 'High performance process and comfort cooling chillers available for immediate nationwide dispatch.',
            path: '/chiller-hire'
          }, {
            title: 'Cooling Hire',
            icon: Wind,
            desc: 'Portable air conditioning units, evaporative coolers and cooling fans for all environments.',
            path: '/cooling-hire'
          }, {
            title: 'Ancillaries',
            icon: Wrench,
            desc: 'Hoses, heat exchangers, fuel tanks, and generators to support your temporary HVAC installation.',
            path: '/ancillaries'
          }, {
            title: 'Industry Sectors',
            icon: Factory,
            desc: 'Specialized solutions tailored for Healthcare, Education, Facilities Management, and more.',
            path: '/services'
          }].map((svc, i) => <Link to={svc.path} key={i} data-id="element-521">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 group cursor-pointer h-full flex flex-col" data-id="element-522">
                  <div className="w-14 h-14 bg-brand-light rounded-lg flex items-center justify-center mb-6 group-hover:bg-brand-green/20 transition-colors" data-id="element-523">
                    <svc.icon className="w-7 h-7 text-brand-dark group-hover:text-brand-green transition-colors" data-id="element-524" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-brand-dark mb-3" data-id="element-525">
                    {svc.title}
                  </h3>
                  <p className="text-gray-600 mb-4 flex-grow" data-id="element-526">{svc.desc}</p>
                  <span className="text-brand-green font-bold text-sm flex items-center group-hover:text-brand-dark transition-colors mt-auto" data-id="element-527">
                    Learn more{' '}
                    <Check className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-528" />
                  </span>
                </motion.div>
              </Link>)}
          </div>
        </div>
      </section>

      <CTASection data-id="element-529" />
    </main>;
}